import { cancelChangeEmail } from '@dealstack/frontend/shared/dataExchange/services/auth.service';
import { getBackendErrorCode } from '@dealstack/frontend/shared/dataExchange/services/backendCodedError';
import { useTranslationAuth } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { ERROR_CODES } from '@dealstack/shared/lib/common/errors';
import { Button, Text } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

type CancelOutcome = 'confirm' | 'pending' | 'cancelled' | 'dead_link' | 'failed';

const COPY: Record<CancelOutcome, { title: string; description: string }> = {
	confirm: {
		title: 'cancel_email_change.confirm.title',
		description: 'cancel_email_change.confirm.description',
	},
	pending: {
		title: 'cancel_email_change.pending.title',
		description: 'cancel_email_change.pending.description',
	},
	cancelled: {
		title: 'cancel_email_change.cancelled.title',
		description: 'cancel_email_change.cancelled.description',
	},
	dead_link: {
		title: 'cancel_email_change.dead_link.title',
		description: 'cancel_email_change.dead_link.description',
	},
	failed: {
		title: 'cancel_email_change.failed.title',
		description: 'cancel_email_change.failed.description',
	},
};

interface Props {
	loginRoute: string;
}

export const CancelEmailChange = ({ loginRoute }: Props) => {
	const tAuth = useTranslationAuth();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const cancelToken = searchParams.get('token');
	const [outcome, setOutcome] = useState<CancelOutcome>('confirm');
	const spentToken = useRef<string | null>(null);

	useEffect(() => {
		if (!cancelToken) {
			setOutcome('dead_link');
		}
	}, [cancelToken]);

	const confirmCancel = () => {
		if (!cancelToken || spentToken.current === cancelToken) {
			return;
		}
		spentToken.current = cancelToken;
		setOutcome('pending');
		cancelChangeEmail({ cancelToken })
			.then(() => {
				setOutcome('cancelled');
			})
			.catch((error: unknown) => {
				if (getBackendErrorCode(error) === ERROR_CODES.AUTH.CHANGE_EMAIL[50006]) {
					setOutcome('dead_link');
					return;
				}
				setOutcome('failed');
			});
	};

	const copy = COPY[outcome];
	const awaitingConfirmation = outcome === 'confirm' || outcome === 'pending';

	return (
		<div className="flex flex-col p-8 gap-8 items-center w-[424px] bg-[#F4F5F6] rounded-2xl border border-[#E9EBED]">
			<div className="flex flex-col items-center">
				<Text size="B3Bold" c="dark.8" className="mb-[12px]">
					{tAuth(copy.title)}
				</Text>
				<Text size="B5Medium" c="dark.0" ta="center">
					{tAuth(copy.description)}
				</Text>
			</div>
			{awaitingConfirmation ? (
				<Button
					type="button"
					variant="primary"
					loading={outcome === 'pending'}
					onClick={confirmCancel}
				>
					{tAuth('cancel_email_change.confirm.action')}
				</Button>
			) : (
				<Button type="button" variant="primary" onClick={() => navigate(loginRoute)}>
					{tAuth('cancel_email_change.back_to_login')}
				</Button>
			)}
		</div>
	);
};
